import Button from 'components/base/Button'
import FormItem from 'components/base/FormItem'
import Input from 'components/base/Input'
import { FC, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { Controller, useForm } from 'react-hook-form'
import { Updater } from 'use-immer'
import { SignUpProcessState, UserFormData } from './lib/types'
import PasswordInput from 'components/base/PasswordInput'
import PhoneNumberInput from 'components/base/PhoneNumberInput'
import { ArrowNarrowRightIcon } from '@heroicons/react/solid'
import { useIntl } from 'lib/intl-utils'
import LegalText from './LegalText'
import Badge from 'components/base/Badge'
import { usePromoCodeContext } from 'components/landing/lib/promoCodeContext'
import PromoCode from 'components/landing/PromoCode'
import Link from 'components/base/Link'
import { match } from 'ts-pattern'
import { amplitude } from 'components/app/Amplitude'
import Separator from 'components/base/Separator'
import { InformationCircleIcon } from '@heroicons/react/outline'
import { useAtomValue, useSetAtom } from 'jotai'
import CaptchaError from 'components/auth/CaptchaError'
import { captchaStatusAtom, resetCaptchaAtom } from 'components/auth/lib/atoms'
import Checkbox from 'components/base/Checkbox'
import { createWbIdSdk } from 'lib/wb-id-sdk'
import { useToast } from 'components/app/NotificationArea'
import { getLegalDocuments } from 'configs/legal'
import { useRouter } from 'next/router'
import { addInternalRedirectSearchParam, getSafeInternalRedirect } from 'lib/redirect'

type Props = {
  processState: SignUpProcessState
  setProcessState: Updater<SignUpProcessState>
  defaultTrialDays: number | null
  error?: string
  onSubmittingChange?: (submitting: boolean) => void
  includeHeader?: boolean
  showVKSignIn?: boolean
  additionalFields?: React.ReactNode
  afterSubmit?: (value: string) => void
  redirect?: string
  plan?: string
  onPromoCodeRemove?: () => void
  hideLegalText?: boolean
  consents?: Pick<UserFormData, 'legalDocumentsConsentReceived' | 'adCommunicationsConsentReceived'>
  beforeSubmit?: () => boolean
}

const UserInfoForm: FC<Props> = ({
  processState,
  setProcessState,
  defaultTrialDays,
  error,
  includeHeader = true,
  showVKSignIn = true,
  additionalFields,
  afterSubmit,
  redirect,
  plan,
  onPromoCodeRemove,
  hideLegalText = false,
  consents,
  beforeSubmit,
  onSubmittingChange,
}) => {
  const ctx = useUserContext()
  const router = useRouter()
  const isPasswordlessSignupHost = ['sellmonitor', 'sellscreen', 'mayak'].includes(
    ctx.theme.hostType
  )
  const promoCode = usePromoCodeContext()
  const { t, lang } = useIntl()
  const legalDocuments = getLegalDocuments(ctx.theme.hostType)
  const showToast = useToast()
  const safeRedirect = getSafeInternalRedirect(redirect)
  const [generalError, setGeneralError] = useState(
    error ? t`login.oauth_error` + ' ' + error : null
  )

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<UserFormData>({ mode: 'onSubmit' })
  useEffect(() => {
    onSubmittingChange?.(isSubmitting)
    return () => onSubmittingChange?.(false)
  }, [isSubmitting, onSubmittingChange])

  const [autoFocusEnabled, setAutoFocusEnabled] = useState(false)
  const legalConsentInputRef = useRef<HTMLInputElement | null>(null)
  const captchaStatus = useAtomValue(captchaStatusAtom)
  const resetCaptcha = useSetAtom(resetCaptchaAtom)

  const legalConsentRequired = ['sellmonitor', 'mayak'].includes(ctx.theme.hostType)
  const password = watch('password')
  const legalConsentMissing =
    legalConsentRequired && !(watch('legalDocumentsConsentReceived') ?? false)

  const reportLegalConsentValidity = useCallback(() => {
    if (!legalConsentMissing) return true
    return legalConsentInputRef.current?.reportValidity() ?? false
  }, [legalConsentMissing])

  const handleWbIdLogin = useCallback(async () => {
    if (!reportLegalConsentValidity()) return

    try {
      const sdk = createWbIdSdk()
      await sdk.login()
    } catch (error) {
      showToast('error', t`error`, t`login.oauth_error`)
    }
  }, [reportLegalConsentValidity, showToast, t])

  const phoneRules = useMemo(
    () => ({
      required: t`required_field`,
      validate: {
        isPossiblePhone: (value?: string | null) =>
          !value || isPossiblePhoneNumber(value) || t`registration.invalid_phone`,
        isAvailable: async (v: string) => {
          const testResult = await ctx.testPhone(v)
          if (testResult === 'available') {
            return true
          }
          return t`registration.phone_taken`
        },
      },
    }),
    [ctx, t]
  )

  const freeDays = promoCode?.freeDays ?? defaultTrialDays ?? 2

  const onUserDataSubmit = useCallback(
    async (data: UserFormData) => {
      if (beforeSubmit && !beforeSubmit()) return
      if (consents) data = { ...data, ...consents }
      if (isPasswordlessSignupHost) {
        window.scrollTo({ top: 0 })

        resetCaptcha()
        const response = await ctx.registerSendAuthCode(data.phone, captchaStatus.token)

        match(response)
          .with({ status: 'AuthCodeSent' }, (response) => {
            setProcessState((s) => {
              s.stage = 'waitingForCode'
              s.data = data
              s.codeType = response.type
              s.phone = data.phone
            })

            afterSubmit?.(data.phone)
          })
          .with({ status: 'AttemptsLimitReached' }, (response) => {
            setGeneralError(
              t`login.attempts_limit_reached` +
                ` ${response.seconds} ` +
                t('second_accusative', { count: response.seconds })
            )
          })
          .with({ status: 'CaptchaFailed' }, () => {
            setGeneralError(t`captcha.try_again`)
          })
          .with({ error: 'FormValidationErrors' } as any, (response) => {
            setGeneralError(t`check_form_for_errors`)
          })
          .otherwise(() => {
            setGeneralError(t`something_went_wrong`)
          })
      }
      if (ctx.theme.hostType === 'uzum') {
        ctx
          .registerUser({
            email: data.email,
            fullName: data.firstName + ' ' + data.lastName,
            phone: data.phone,
            plainPassword: data.password,
          })
          .then(() => {
            window.location.href = safeRedirect
              ? addInternalRedirectSearchParam('/trial/welcome/', 'redirect', safeRedirect)
              : '/trial/welcome/'
          })
      }
    },
    [afterSubmit, captchaStatus, isPasswordlessSignupHost, safeRedirect, beforeSubmit, consents]
  )

  useEffect(() => {
    if (!processState.data) return
    reset(processState.data)
  }, [processState.data])

  useEffect(() => {
    setTimeout(() => {
      setAutoFocusEnabled(true)
    }, 100)
  }, [])

  useEffect(() => {
    if (!router.isReady) return

    amplitude.track('Signup Started', {
      host: ctx.theme.hostType,
      path: router.asPath.split('?')[0],
    })
  }, [router.isReady])

  return (
    <>
      {includeHeader && (
        <div className="mt-20">
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:w-max">
            <h2 className="text-lg font-semibold text-gray-900 shrink-0">{t`registration.get_started`}</h2>
            <Badge
              className={c`ring-2 shrink-0 ${
                ctx.theme.hostType === 'mayak'
              } ring-amber-300 | ring-lime-300`}
            >
              {freeDays} {t('day', { count: freeDays })}
            </Badge>
            {['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && promoCode && (
              <PromoCode
                compact
                alwaysShortText
                promoCode={promoCode}
                onRemove={onPromoCodeRemove}
              />
            )}
          </div>
          <p className="mt-2 text-sm text-gray-700">
            {t`registration.already_registered`}{' '}
            <Link href="/login/" className="font-medium text-primary-600 hover:underline">
              {t`registration.sign_in`}
            </Link>{' '}
            {t`registration.to_your_account`}
          </p>
          {ctx.theme.hostType === 'sellmonitor' &&
            (plan === 'extended' || plan === 'additional-platform') && (
              <div className="flex gap-2 mt-3">
                <InformationCircleIcon
                  className="size-6 text-gray-400 -mt-0.5"
                  aria-hidden="true"
                />
                <p className="text-sm text-gray-700">
                  {t(
                    plan === 'extended'
                      ? 'auth_legal.consultation_extended'
                      : 'auth_legal.consultation_platform'
                  )}
                </p>
              </div>
            )}
        </div>
      )}
      <form
        onSubmit={handleSubmit(onUserDataSubmit)}
        className="grid grid-cols-1 mt-10 gap-x-6 gap-y-6 sm:grid-cols-2"
      >
        {additionalFields}
        {!['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && (
          <FormItem label={t`first_name`} error={errors.firstName?.message}>
            <Input
              {...register('firstName', { required: t`required_field` })}
              hasError={Boolean(errors.firstName?.message)}
              type="text"
              autoComplete="given-name"
            />
          </FormItem>
        )}
        {!['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && (
          <FormItem label={t`last_name`} error={errors.lastName?.message}>
            <Input
              {...register('lastName', { required: t`required_field` })}
              hasError={Boolean(errors.lastName?.message)}
              type="text"
              autoComplete="family-name"
            />
          </FormItem>
        )}
        {!['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && (
          <FormItem label="Email" error={errors.email?.message} className="col-span-full">
            <Input
              {...register('email', {
                required: t`required_field`,
                validate: {
                  isValid: (v) =>
                    /^[^\.@\s](?:[^@\s]*[^\.@\s])?@[^\s@]+\.[^\s@]+$/.test(v) ||
                    t`registration.invalid_email`,
                  isAvailable: async (v) => {
                    const testResult = await ctx.testEmail(v)
                    if (testResult === 'available') {
                      return true
                    }
                    return t`registration.email_taken`
                  },
                },
              })}
              hasError={Boolean(errors.email?.message)}
              type="email"
              autoComplete="email"
            />
          </FormItem>
        )}
        <FormItem label={t`phone`} error={errors.phone?.message} className="col-span-full">
          <Controller
            name="phone"
            control={control}
            rules={phoneRules}
            render={({ field }) => (
              <PhoneNumberInput
                {...field}
                ref={field.ref}
                value={field.value ?? null}
                autoFocus={autoFocusEnabled && ctx.theme.hostType !== 'sellscreen'}
                hasError={Boolean(errors.phone?.message)}
              />
            )}
          />
        </FormItem>
        {!['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && (
          <FormItem label={t`password`} error={errors.password?.message} className="">
            <PasswordInput
              {...register('password', {
                required: t`required_field`,
                minLength: {
                  value: 8,
                  message: t`password_less_than_8`,
                },
                maxLength: {
                  value: 191,
                  message: t`password_more_than_191`,
                },
                validate: {
                  hasNumber: (v) => /\d/.test(v) || t`password_are_not_match_numbers`,
                  hasLetter: (v) => /[a-z]/i.test(v) || t`password_are_not_match_latin`,
                },
              })}
              hasError={Boolean(errors.password?.message)}
              autoComplete="new-password"
            />
          </FormItem>
        )}
        {!['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && (
          <FormItem label={t`confirm_password`} error={errors.repeatPassword?.message} className="">
            <PasswordInput
              {...register('repeatPassword', {
                required: t`required_field`,
                validate: {
                  matchesPassword: (v) => v === password || t`passwords_dont_match`,
                },
              })}
              hasError={Boolean(errors.repeatPassword?.message)}
              autoComplete="new-password"
            />
          </FormItem>
        )}
        <div className="space-y-2 col-span-full">
          {generalError && <p className="text-red-600">{generalError}</p>}
          <CaptchaError />
          <Button
            disabled={
              ['sellmonitor', 'sellscreen', 'mayak'].includes(ctx.theme.hostType) &&
              !captchaStatus.passed
            }
            type="submit"
            theme="primary"
            className="justify-center w-full mt-0 space-x-1"
          >
            <span>{t`sign_up`} </span>
            <ArrowNarrowRightIcon className="w-4 h-4" />
          </Button>
          {ctx.theme.hostType === 'sellmonitor' && showVKSignIn && (
            <>
              <div className="relative">
                <Separator orientation="horizontal" className="my-6" />
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-background px-2.5 bg-white text-xs text-gray-500">
                  {t`or`}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex flex-col flex-1">
                  <button
                    type="button"
                    onClick={handleWbIdLogin}
                    className="focus:ring-primary-500 font-medium text-sm rounded-md h-[38px] w-full flex items-center justify-center gap-[7px] hover:bg-[#F5F5F7] bg-white border border-black/[0.12]"
                  >
                    <img src="/next/img/oauth/wb_logo_accent.svg" className="h-4" />
                    <span>{t`login.sign_in_with_wb_id`}</span>
                  </button>
                </div>
                <div className="flex flex-col flex-1">
                  <a
                    href="/oauth/start/vk/"
                    onClick={(event) => {
                      if (reportLegalConsentValidity()) return
                      event.preventDefault()
                    }}
                    className="focus:ring-primary-500 font-medium text-sm rounded-md h-[38px] w-full flex items-center justify-center gap-[7px] hover:bg-[#F5F5F7] bg-white border border-black/[0.12]"
                  >
                    <img src="/next/img/oauth/vk_logo_blue.svg" className="size-6" />
                    <span>{t`login.sign_in_with_vk_id`}</span>
                  </a>
                </div>
              </div>
            </>
          )}
          {!hideLegalText && (
            <div
              className={c`${includeHeader} px-6 !mt-10 | px-4 !mt-8 text-xs text-left text-gray-500`}
            >
              {legalConsentRequired && (
                <>
                  <p className="w-full text-left">
                    <LegalText
                      message={t`auth_legal.sign_up_offer_privacy`}
                      links={{
                        offer: { href: legalDocuments.offer, label: t`auth_legal.offer` },
                        privacy: {
                          href: legalDocuments.privacyPolicy,
                          label: t`auth_legal.privacy`,
                        },
                      }}
                    />
                  </p>
                  <label className="flex items-start w-full gap-2 mt-4 cursor-pointer">
                    <Controller
                      name="legalDocumentsConsentReceived"
                      control={control}
                      defaultValue={false}
                      rules={{ required: legalConsentRequired }}
                      render={({ field: { value, onChange, ref, ...field } }) => (
                        <Checkbox
                          {...field}
                          ref={(input) => {
                            ref(input)
                            legalConsentInputRef.current = input
                          }}
                          checked={value ?? false}
                          required={legalConsentRequired}
                          onChange={onChange}
                        />
                      )}
                    />
                    <span className="text-left">
                      <LegalText
                        message={t`auth_legal.personal_data_consent`}
                        links={{
                          consent: {
                            href:
                              ctx.theme.hostType === 'mayak'
                                ? 'https://docs.google.com/document/d/1UImcvbMSXsD-_HSfClloZG6hhyqUr5HXvg7dvx9L75c/edit?usp=sharing'
                                : '/doc/sellmonitor/ru/personal_data_processing_consent.pdf',
                            label: t`auth_legal.agree`,
                          },
                        }}
                      />
                    </span>
                  </label>
                </>
              )}
              {ctx.theme.hostType === 'vtb' && (
                <LegalText
                  message={t`auth_legal.sign_up_offer_privacy`}
                  links={{
                    offer: { href: legalDocuments.offer, label: t`auth_legal.offer` },
                    privacy: { href: legalDocuments.privacyPolicy, label: t`auth_legal.privacy` },
                  }}
                />
              )}
              {ctx.theme.hostType === 'sellscreen' && (
                <LegalText
                  message={t`auth_legal.sign_up_offer_privacy`}
                  links={{
                    offer: { href: '/doc/sellscreen/en/terms.pdf', label: t`auth_legal.terms` },
                    privacy: {
                      href: '/doc/sellscreen/en/privacy_policy.pdf',
                      label: t`auth_legal.privacy`,
                    },
                  }}
                />
              )}
              {['sellmonitor', 'mayak'].includes(ctx.theme.hostType) && (
                <label className="flex items-start w-full gap-2 mt-3 cursor-pointer">
                  <Controller
                    name="adCommunicationsConsentReceived"
                    control={control}
                    defaultValue={false}
                    rules={{ required: false }}
                    render={({ field: { value, onChange, ...field } }) => (
                      <Checkbox {...field} checked={value ?? false} onChange={onChange} />
                    )}
                  />
                  <span className="text-left">
                    <LegalText
                      message={t`auth_legal.marketing_consent`}
                      links={{
                        consent: {
                          href:
                            ctx.theme.hostType === 'mayak'
                              ? 'https://docs.google.com/document/d/1lCJ2Yt7jbhVC3xMfG8sQideTBrdOgxJMPFXmvTcFU_c/edit?usp=sharing'
                              : '/doc/sellmonitor/ru/marketing_communications_consent.pdf',
                          label: t`auth_legal.agree`,
                        },
                      }}
                    />
                  </span>
                </label>
              )}
            </div>
          )}
        </div>
      </form>
    </>
  )
}

export default UserInfoForm
